import React, { useRef, useState, useEffect } from "react"
import Slider from "react-slick"
import { useTranslation } from "@zestia/gatsby-plugin-react-i18next"

/**
 * QuotesCarousel component
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {Array<{ kicker: string | React.ReactNode, quote: string, name: string, role: string }>} props.quotes
 *
 * @returns {React.ReactElement} QuotesCarousel component
 */
const QuotesCarousel = ({ children, quotes, className }) => {
  const { t } = useTranslation()
  const [slideIndex, setSlideIndex] = useState(0)
  let sliderRef = useRef(null)

  const [screenReaderSlideDescription, setScreenReaderSlideDescription] =
    useState("")

  useEffect(() => {
    // Note: We set the description here as a whole, as the aria-live region will only read out what has changed (currentSlide - ie. 2) instead of the entire sentence otherwise.
    setScreenReaderSlideDescription(
      `Slide ${slideIndex + 1} of ${quotes.length}`,
    )
  }, [slideIndex])

  return (
    <div className={`quotes-carousel row ${className ?? ""}`}>
      <div className="quotes-carousel__content col-xs-12 col-md-5">
        {children}
      </div>

      <p className="screen-reader-only" aria-live="polite">
        {screenReaderSlideDescription}
      </p>

      {quotes.length > 0 && (
        <div className="quotes-carousel__slider col-xs-12 col-md-7">
          <Slider
            ref={slider => {
              sliderRef = slider
            }}
            dots={false}
            arrows={false}
            beforeChange={(_, newIndex) => setSlideIndex(newIndex)}
            fade={true}
          >
            {quotes.map((quote, index) => (
              <div className="quotes-carousel__slide" key={index}>
                {typeof quote.kicker === "string" ? (
                  <h3 className="h5">{quote.kicker}</h3>
                ) : (
                  <div className="quotes-carousel__slide__logo mb-8">
                    <quote.kicker />
                  </div>
                )}

                <p className="p--lg">"{quote.quote}"</p>
              </div>
            ))}
          </Slider>

          {/* Current quote info, and controls */}
          <div className="quotes-carousel__footer mt-8">
            {/* Current slide info */}
            <div className="quotes-carousel__info">
              <p className="mb-0 p">{quotes[slideIndex].name}</p>

              <p className="p--sm">{quotes[slideIndex].role}</p>
            </div>

            {/* Controls */}
            <div className="quotes-carousel__controls">
              <button
                className="quotes-carousel__control"
                onClick={() => sliderRef.slickPrev()}
                aria-label={t("common.previous-slide")}
              >
                <svg
                  width="26"
                  height="26"
                  viewBox="0 0 26 26"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M12.998 26C20.178 26 25.998 20.18 25.998 13C25.998 5.82 20.178 1.39855e-06 12.9981 7.70853e-07C5.81805 1.43157e-07 -0.00194945 5.82 -0.00195008 13C-0.00195071 20.18 5.81805 26 12.998 26ZM7.29138 12.2933C7.10411 12.4808 6.99893 12.735 6.99893 13C6.99893 13.265 7.10411 13.5192 7.29138 13.7067L11.2914 17.7067C11.3829 17.8049 11.4933 17.8837 11.616 17.9384C11.7387 17.993 11.8711 18.0224 12.0054 18.0248C12.1396 18.0272 12.273 18.0025 12.3975 17.9522C12.522 17.9019 12.6351 17.827 12.7301 17.732C12.8251 17.6371 12.8999 17.524 12.9502 17.3995C13.0005 17.2749 13.0252 17.1416 13.0228 17.0073C13.0205 16.873 12.9911 16.7406 12.9364 16.6179C12.8818 16.4953 12.803 16.3849 12.7047 16.2933L10.4114 14L17.998 14C18.2633 14 18.5176 13.8946 18.7052 13.7071C18.8927 13.5196 18.998 13.2652 18.998 13C18.998 12.7348 18.8927 12.4804 18.7052 12.2929C18.5176 12.1054 18.2633 12 17.998 12L10.4114 12L12.7047 9.70667C12.803 9.61512 12.8818 9.50472 12.9364 9.38205C12.9911 9.25938 13.0205 9.12697 13.0228 8.9927C13.0252 8.85843 13.0005 8.72505 12.9502 8.60054C12.8999 8.47602 12.8251 8.36291 12.7301 8.26795C12.6351 8.17299 12.522 8.09813 12.3975 8.04784C12.273 7.99754 12.1396 7.97284 12.0054 7.97521C11.8711 7.97758 11.7387 8.00697 11.616 8.06163C11.4933 8.11628 11.3829 8.19508 11.2914 8.29333L7.29138 12.2933Z"
                    fill="#AFAFAE"
                  />
                </svg>
              </button>

              <button
                className="quotes-carousel__control"
                onClick={() => sliderRef.slickNext()}
                aria-label={t("common.next-slide")}
              >
                <svg
                  width="26"
                  height="26"
                  viewBox="0 0 26 26"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M13.002 26C5.82195 26 0.00195262 20.18 0.00195199 13C0.00195136 5.82 5.82195 1.39855e-06 13.0019 7.70853e-07C20.1819 1.43157e-07 26.0019 5.82 26.002 13C26.002 20.18 20.182 26 13.002 26ZM18.7086 12.2933C18.8959 12.4808 19.0011 12.735 19.0011 13C19.0011 13.265 18.8959 13.5192 18.7086 13.7067L14.7086 17.7067C14.6171 17.8049 14.5067 17.8837 14.384 17.9384C14.2613 17.993 14.1289 18.0224 13.9946 18.0248C13.8604 18.0272 13.727 18.0025 13.6025 17.9522C13.478 17.9019 13.3649 17.827 13.2699 17.732C13.1749 17.6371 13.1001 17.524 13.0498 17.3995C12.9995 17.2749 12.9748 17.1416 12.9772 17.0073C12.9795 16.873 13.0089 16.7406 13.0636 16.6179C13.1182 16.4953 13.197 16.3849 13.2953 16.2933L15.5886 14L8.00195 14C7.73674 14 7.48238 13.8946 7.29484 13.7071C7.10731 13.5196 7.00195 13.2652 7.00195 13C7.00195 12.7348 7.10731 12.4804 7.29484 12.2929C7.48238 12.1054 7.73674 12 8.00195 12L15.5886 12L13.2953 9.70667C13.197 9.61512 13.1182 9.50472 13.0636 9.38205C13.0089 9.25938 12.9795 9.12697 12.9772 8.9927C12.9748 8.85843 12.9995 8.72505 13.0498 8.60054C13.1001 8.47602 13.1749 8.36291 13.2699 8.26795C13.3649 8.17299 13.478 8.09813 13.6025 8.04784C13.727 7.99754 13.8604 7.97284 13.9946 7.97521C14.1289 7.97758 14.2613 8.00697 14.384 8.06163C14.5067 8.11628 14.6171 8.19508 14.7086 8.29333L18.7086 12.2933Z"
                    fill="#AFAFAE"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default QuotesCarousel
